import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, GraduationCap, Award, Target,
  Plus, BarChart3, Megaphone, BookOpen,
  Mail, Mic, FileText, Download,
} from 'lucide-react';
import api from '../../services/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

const getInitials = (firstName, lastName) => {
  const a = (firstName || '').charAt(0).toUpperCase();
  const b = (lastName || '').charAt(0).toUpperCase();
  return (a + b) || '?';
};

const AVATAR_COLORS = ['#1a5d3a', '#2E7D32', '#1565C0', '#6A1B9A', '#C62828', '#00695C'];
const avatarColor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const formatDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const useLiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  return now;
};

// ─── sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, iconColor, href, loading }) => {
  const inner = (
    <div
      className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${iconColor}18` }}
        >
          <Icon size={22} style={{ color: iconColor }} />
        </div>
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-1">
        {loading ? (
          <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
        ) : (
          value ?? '—'
        )}
      </div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
};

const InitialsAvatar = ({ firstName, lastName, email }) => (
  <div
    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
    style={{ backgroundColor: avatarColor(email) }}
  >
    {getInitials(firstName, lastName)}
  </div>
);

// ─── main component ───────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const now = useLiveClock();

  const [stats, setStats] = useState({ users: null, colleges: null, scholarships: null, leads: null });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [exportingCSV, setExportingCSV] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const [usersCountRes, collegesRes, scholarshipsRes, analyticsRes] = await Promise.all([
        api.get('/api/admin/users/count'),
        api.get('/api/colleges?limit=1'),
        api.get('/api/scholarships?limit=1'),
        api.get('/api/admin/analytics'),
      ]);
      setStats({
        users: usersCountRes.data.total,
        colleges: collegesRes.data.total,
        scholarships: scholarshipsRes.data.total,
        leads: analyticsRes.data.stats.total_leads,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadRecentUsers = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/users?limit=5');
      setRecentUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load recent users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadRecentLeads = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/leads');
      const leads = Array.isArray(res.data) ? res.data : [];
      setRecentLeads(leads.slice(0, 5));
    } catch (err) {
      console.error('Failed to load recent leads:', err);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadRecentUsers();
    loadRecentLeads();
  }, [loadStats, loadRecentUsers, loadRecentLeads]);

  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      const res = await api.get('/api/admin/leads/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExportingCSV(false);
    }
  };

  const dateLabel = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const timeLabel = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const quickActions = [
    { label: 'Add College',        icon: GraduationCap, to: '/admin/colleges/new'          },
    { label: 'Add Scholarship',    icon: Award,         to: '/admin/scholarships/new'       },
    { label: 'Add Article',        icon: FileText,      to: '/admin/articles/new'           },
    { label: 'Add Magazine Issue', icon: BookOpen,      to: '/admin/magazine/new'           },
    { label: 'Add Newsletter',     icon: Mail,          to: '/admin/newsletter/new'         },
    { label: 'Add Podcast',        icon: Mic,           to: '/admin/podcast/new'            },
    { label: 'Announcements',      icon: Megaphone,     to: '/admin/announcement-bar'       },
    { label: 'View Analytics',     icon: BarChart3,     to: '/admin/analytics'              },
    { label: 'Export Leads CSV',   icon: Download,      onClick: handleExportCSV, isButton: true },
  ];

  return (
    <div className="p-8 space-y-8" data-testid="admin-dashboard">

      {/* Top bar */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1a1a1a' }} data-testid="dashboard-title">
            Command Center
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
            StudentSignal live platform intelligence
          </p>
        </div>
        <div className="text-right" data-testid="dashboard-clock">
          <div className="text-lg font-semibold text-gray-800">{timeLabel}</div>
          <div className="text-xs text-gray-500">{dateLabel}</div>
        </div>
      </div>

      {/* ROW 1 — Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Students"
          value={stats.users}
          iconColor="#1a5d3a"
          href="/admin/users"
          loading={loadingStats}
        />
        <StatCard
          icon={GraduationCap}
          label="Total Colleges"
          value={stats.colleges}
          iconColor="#1a5d3a"
          href="/admin/colleges"
          loading={loadingStats}
        />
        <StatCard
          icon={Award}
          label="Total Scholarships"
          value={stats.scholarships}
          iconColor="#f5a623"
          href="/admin/scholarships"
          loading={loadingStats}
        />
        <StatCard
          icon={Target}
          label="Total Leads"
          value={stats.leads}
          iconColor="#f5a623"
          href="/admin/leads"
          loading={loadingStats}
        />
      </div>

      {/* ROW 2 — Recent Students + Recent Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* Recent Students — 60% */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="recent-students-panel">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Recent Students</h2>
            <Link
              to="/admin/users"
              className="text-sm font-medium transition-colors"
              style={{ color: '#1a5d3a' }}
              data-testid="view-all-students-link"
            >
              View All
            </Link>
          </div>
          {loadingUsers ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3 animate-pulse">
                  <div className="w-9 h-9 rounded-lg bg-gray-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentUsers.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No students registered yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors" data-testid={`student-row-${user.id}`}>
                  <InitialsAvatar firstName={user.first_name} lastName={user.last_name} email={user.email} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  {user.state && (
                    <div className="text-xs text-gray-400 hidden sm:block">{user.state}</div>
                  )}
                  <span
                    className="text-xs px-2 py-0.5 rounded font-medium flex-shrink-0"
                    style={
                      user.onboarding_completed
                        ? { backgroundColor: '#FEF3C7', color: '#92400E' }
                        : { backgroundColor: '#F3F4F6', color: '#6B7280' }
                    }
                  >
                    {user.onboarding_completed ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Leads — 40% */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="recent-leads-panel">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Recent Leads</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportCSV}
                disabled={exportingCSV}
                className="text-xs font-medium flex items-center gap-1 transition-colors"
                style={{ color: '#1a5d3a' }}
                data-testid="export-csv-btn"
              >
                <Download size={13} />
                {exportingCSV ? 'Exporting...' : 'CSV'}
              </button>
              <Link
                to="/admin/leads"
                className="text-sm font-medium transition-colors"
                style={{ color: '#1a5d3a' }}
                data-testid="view-all-leads-link"
              >
                View All
              </Link>
            </div>
          </div>
          {loadingLeads ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="space-y-1.5 animate-pulse">
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : recentLeads.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No leads submitted yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentLeads.map((lead) => (
                <div key={lead.id} className="px-6 py-3 hover:bg-gray-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {[lead.first_name, lead.last_name].filter(Boolean).join(' ')}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{lead.email}</div>
                  {lead.college_name && (
                    <div className="text-xs mt-0.5 truncate" style={{ color: '#1a5d3a' }}>
                      {lead.college_name}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-0.5">{formatDate(lead.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ROW 3 — Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-5">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            const base = "flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-transparent hover:border-gray-200 transition-all text-center group";
            if (action.isButton) {
              return (
                <button
                  key={action.label}
                  type="button"
                  onClick={action.onClick}
                  className={base}
                  style={{ backgroundColor: '#F9FAFB' }}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
                    <Icon size={20} style={{ color: '#1a5d3a' }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 leading-tight">{action.label}</span>
                </button>
              );
            }
            return (
              <Link
                key={action.label}
                to={action.to}
                className={base}
                style={{ backgroundColor: '#F9FAFB' }}
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
                  <Icon size={20} style={{ color: '#1a5d3a' }} />
                </div>
                <span className="text-xs font-medium text-gray-700 leading-tight">{action.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;
